import { AxiosResponse, isAxiosError } from 'axios';
import { createLogger } from 'logging';

import {
    CreateUserRequest,
    UpdateAbuseStatus200Response,
    UpdateAbuseStatusRequest,
    UpdateUserRequest,
    User,
    UserAbuseLog,
    Permission,
    UserYoutubeChannel,
    UserApiUpdatePermissionsRequest,
    UserApiDeletePermissionRequest,
    DeletePermissionResourceEnum,
    OptIns,
    OptInName,
} from '@videoblocks/user-service-client';

import { CreateUserOutcome, CreateUserResult } from './CreateUserResult';
import { isErrorMessage } from './isErrorMessage';
import { userApi } from './userServiceConfig';

const logger = createLogger('UserApi');

const createUser = async (createUserRequest: CreateUserRequest): Promise<CreateUserResult> => {
    try {
        const result = await userApi.createUser({ createUserRequest });
        return { outcome: CreateUserOutcome.UserCreated, user: result.data };
    } catch (error) {
        if (isAxiosError(error)) {
            const { status } = error?.response ?? {};
            if (status === 302) {
                return { outcome: CreateUserOutcome.UserAlreadyRegistered };
            }
            if (status === 403 || status === 409) {
                const data: unknown = error.response?.data;
                const errorMessage = isErrorMessage(data) ? data.message : error.message;
                return { outcome: CreateUserOutcome.Error, error: errorMessage };
            }
        }
        throw error;
    }
};

const getUserBySession = async (sessionToken: string): Promise<User | null> => {
    try {
        const result = (await userApi.searchUsers({ session: sessionToken })) as AxiosResponse<User>;
        return result.data;
    } catch (error) {
        if (isAxiosError(error)) {
            const { status } = error.response || {};
            if (status !== 404) {
                throw error;
            }
        }
    }
    return null;
};

const checkUserPermissionByNameAndResource = async (
    userId: number,
    permission: string,
    resource: string,
): Promise<boolean> => {
    try {
        const permissions = await getUserPermissions(userId);
        return permissions.some((perm) => perm.permissionName === permission && perm.resource === resource);
    } catch (error) {
        if (isAxiosError(error)) {
            const { status } = error.response || {};
            if (status !== 404) {
                throw error;
            }
        }
        return false;
    }
};

const getUserPermissionValueByNameAndResource = async (
    userId: number,
    permission: string,
    resource: string,
): Promise<string | null> => {
    try {
        const permissions = await getUserPermissions(userId);
        const requestedPermission = permissions.find(
            (perm) => perm.permissionName === permission && perm.resource === resource,
        );
        return requestedPermission?.value ?? null;
    } catch (error) {
        if (isAxiosError(error)) {
            const { status } = error.response || {};
            if (status !== 404) {
                throw error;
            }
        }
        return null;
    }
};

const getUserPermissions = async (userId: number): Promise<Permission[]> => {
    try {
        const result = await userApi.getPermissions({ userId: userId });
        return result.data;
    } catch (error) {
        if (isAxiosError(error)) {
            const { status } = error.response || {};
            if (status !== 404) {
                throw error;
            }
        }
        return [];
    }
};

const updateUserPermission = async (
    userId: number,
    permissionName: string,
    permissionNewValue: string,
): Promise<Permission | null> => {
    if (!permissionName || !permissionNewValue) {
        return null; // Need to actually update something
    }
    try {
        const request = {
            userId,
            updatePermissionsRequest: {
                permissionName: permissionName,
                value: permissionNewValue,
            },
        } satisfies UserApiUpdatePermissionsRequest;
        const result = await userApi.updatePermissions(request);
        return result.data;
    } catch (error) {
        if (isAxiosError(error)) {
            const { status } = error.response || {};
            if (status !== 404) {
                throw error;
            }
        }
        return null;
    }
};

const deleteUserPermission = async (
    userId: number,
    permissionName: string,
    resource?: DeletePermissionResourceEnum,
): Promise<number | null> => {
    if (!userId || !permissionName) {
        return null;
    }
    try {
        const request = {
            userId,
            permissionName,
            ...(resource && { resource }),
        } satisfies UserApiDeletePermissionRequest;
        const result = await userApi.deletePermission(request);
        return result.data;
    } catch (error: unknown) {
        if (isAxiosError(error)) {
            const { status } = error.response || {};
            if (status === 404) {
                return null; // User not found
            }
        }
        throw error;
    }
};

const getUserById = async (userId: number): Promise<User | null> => {
    try {
        const result = await userApi.getUserById({ userId });
        return result.data;
    } catch (error) {
        if (isAxiosError(error)) {
            const { status } = error.response || {};
            if (status === 404) {
                return null; // User not found
            }
        }
        throw error;
    }
};

const updateUser = async (userId: number, updateData: UpdateUserRequest): Promise<User> => {
    const result = await userApi.updateUser({
        userId,
        updateUserRequest: updateData,
    });
    return result.data;
};

const getUserYoutubeChannel = async (userId: number): Promise<UserYoutubeChannel[]> => {
    const result = await userApi.getUserYoutubeChannels({ userId });
    return result.data;
};

const updateUserYoutubeChannel = async (userId: number, youtubeChannels: string[]): Promise<UserYoutubeChannel[]> => {
    const [youtubeChannel1, youtubeChannel2] = youtubeChannels;
    try {
        const result = await userApi.updateUserYoutubeChannel({
            userId,
            updateUserYoutubeChannelRequest: {
                youtube_channel_id_1: youtubeChannel1,
                youtube_channel_id_2: youtubeChannel2,
            },
        });
        return result.data;
    } catch (err) {
        if (isAxiosError(err)) {
            const responseData: unknown = err.response?.data;
            const errorMessage = isErrorMessage(responseData) ? responseData.message : err.message;
            throw new Error(errorMessage);
        }
        throw err;
    }
};

const getAbuseLog = async (userId: number): Promise<UserAbuseLog | null> => {
    try {
        const result = await userApi.getAbuseLogsByUserId({ userId });
        return result.data;
    } catch (e: unknown) {
        if (isAxiosError(e)) {
            if (e.response?.status === 404) {
                logger.info({ message: e.message }, 'No abuse status found for the user.');
                return null;
            }
        }
        throw e;
    }
};

const updateAbuseStatus = async (
    request: UpdateAbuseStatusRequest,
): Promise<{ success: boolean; message?: string }> => {
    try {
        const result = await userApi.updateAbuseStatus({
            updateAbuseStatusRequest: request,
        });
        return {
            success: result.data.success ?? false,
            message: result.data.message,
        };
    } catch (e: unknown) {
        if (isAxiosError(e) && e.response && e.response.status === 400) {
            const responseData: unknown = e.response.data;
            const message = isErrorMessage(responseData) ? responseData.message : e.message;
            logger.warn({ message }, 'Error updating abuse status.');
            return { success: false, message };
        }
        throw e;
    }
};

const getUserOptIns = async (userId: number): Promise<OptIns | null> => {
    try {
        const result = await userApi.getOptIns({ userId });
        return result.data;
    } catch (error) {
        if (isAxiosError(error)) {
            const { status } = error.response || {};
            if (status === 404) {
                return null;
            }
        }
        throw error;
    }
};

const updateUserOptIns = async (userId: number, optInName: OptInName): Promise<OptIns | null> => {
    try {
        const result = await userApi.updateOptIns({
            userId,
            updateOptInsRequest: {
                optIn: optInName,
            },
        });
        return result.data;
    } catch (error) {
        if (isAxiosError(error)) {
            const { status } = error.response || {};
            if (status === 404) {
                return null;
            }
        }
        throw error;
    }
};

export const UserApi = {
    createUser,
    getUserBySession,
    getUserById,
    updateUser,
    getUserYoutubeChannel,
    updateUserYoutubeChannel,
    getAbuseLog,
    updateAbuseStatus,
    getUserPermissions,
    updateUserPermission,
    deleteUserPermission,
    checkUserPermissionByNameAndResource,
    getUserPermissionValueByNameAndResource,
    getUserOptIns,
    updateUserOptIns,
};

export type { OptIns, OptInName };
